import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { MissingRuntimeConfigError, resolveEnvironment, resolveRuntimeConfig } from '@/config/runtimeConfig';
import { App, createDefaultHttpClient } from '@/App';
import { ConfigurationError } from '@/ui/ConfigurationError';
import { loadBrandFonts } from '@/ui/fonts';
import { darkTheme, lightTheme } from '@/ui/theme';

/**
 * Point d'entree.
 *
 * L'URL de l'API est resolue **avant** de monter l'application. Si elle manque,
 * l'application ne demarre pas : elle affiche un ecran qui dit quelle variable
 * d'environnement renseigner. Le contraire — demarrer et laisser chaque appel
 * echouer — produirait un backoffice qui s'affiche, propose une connexion, et
 * repond « échec » sans jamais nommer la cause.
 *
 * Le premier rendu attend la police de marque, dans la limite d'un delai court
 * (`ui/fonts.ts`) : la page d'amorcage s'affiche ainsi directement en Nunito, sans
 * la bascule visible police systeme -> police de marque.
 */

const container = document.querySelector('#root');

if (container === null) {
  throw new Error("L'élément #root est absent de index.html.");
}

const root = createRoot(container);

const render = (): void => {
  try {
    const { apiUrl } = resolveRuntimeConfig();

    root.render(
      <StrictMode>
        <App http={createDefaultHttpClient(apiUrl)} environment={resolveEnvironment()} />
      </StrictMode>,
    );
  } catch (cause) {
    if (!(cause instanceof MissingRuntimeConfigError)) {
      throw cause;
    }

    // React Admin n'est pas monte : le theme est fourni a la main, et suit la
    // preference du systeme comme le fait React Admin une fois demarre.
    const prefersDark = globalThis.matchMedia('(prefers-color-scheme: dark)').matches;

    root.render(
      <StrictMode>
        <ThemeProvider theme={createTheme(prefersDark ? darkTheme : lightTheme)}>
          <CssBaseline />
          <ConfigurationError message={cause.message} />
        </ThemeProvider>
      </StrictMode>,
    );
  }
};

void loadBrandFonts().then(render);
